import React from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  Paper,
  Chip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MarketingSiteFooter from "./MarketingSiteFooter";
import MarketingSiteHeader from "./MarketingSiteHeader";

/**
 * Expanded industry narratives for the public use-cases page (B2B meeting-to-document workflows).
 * @type {ReadonlyArray<{
 *   id: string,
 *   industry: string,
 *   summary: string,
 *   painPoints: string[],
 *   quillHelps: string[],
 *   deliverables: string[],
 * }>}
 */
const INDUSTRY_USE_CASES = [
  {
    id: "professional-services",
    industry: "Professional services & consulting",
    summary:
      "Engagements live or die on crisp timelines, scoped work, and executive-ready readouts. QuillAI ties every workshop and steering call to the right client and project so deliverables stay aligned with what was actually agreed.",
    painPoints: [
      "Discovery and sales calls spawn promises that are hard to trace back when it is time to write the SOW.",
      "Junior staff redo the same proposal structure while partners re-explain scope in email.",
      "Executive summaries and recap emails lag the meeting by days.",
    ],
    quillHelps: [
      "Link sessions to customer and project records so drafts pull from the correct transcripts and notes.",
      "Reuse firm templates for phased timelines, assumptions, and change-control language.",
      "Turn recurring meeting series into consistent status readouts without copying from last week’s deck.",
    ],
    deliverables: [
      "Statements of work and change orders",
      "Phased delivery timelines and milestone plans",
      "Steering-committee recaps and executive summaries",
    ],
  },
  {
    id: "technology-saas",
    industry: "Technology & SaaS (sales and solutions)",
    summary:
      "Complex deals mix product demos, security reviews, and custom scope. QuillAI helps solutions and sales engineers produce repeatable proposals and handoff packs so post-sales starts with structured context, not a thread of screenshots.",
    painPoints: [
      "Technical wins are lost in translation when AE notes do not match what presales promised.",
      "Security questionnaires and RFP fragments scatter across drives and chat.",
      "Handoffs to implementation omit edge cases discussed only on one Zoom.",
    ],
    quillHelps: [
      "Generate first drafts of proposals and solution outlines from discovery and demo transcripts.",
      "Keep templates for pricing narratives, SLA language, and implementation assumptions in one library.",
      "Produce customer-facing summaries that cite what was said on which call (for internal review before sending).",
    ],
    deliverables: [
      "Scoped proposals and order forms",
      "Security / diligence response drafts aligned to your standard answers",
      "Sales-to-delivery handoff briefs",
    ],
  },
  {
    id: "financial-services",
    industry: "Financial services & corporate finance",
    summary:
      "Whether you are responding to institutional clients or internal stakeholders, finance teams need controlled language and clear audit trails from committee meetings to signed documents.",
    painPoints: [
      "Credit and risk committees generate decisions that must be reflected accurately in engagement letters and memos.",
      "Repeated client meetings produce overlapping versions of the same narrative.",
      "Regulatory-facing wording cannot be improvised ad hoc in email.",
    ],
    quillHelps: [
      "Anchor drafts in approved templates so sensitive terms stay consistent.",
      "Start from meeting transcripts and notes stored against the right customer relationship.",
      "Iterate in rich text before exporting to your document control or e-sign workflow.",
    ],
    deliverables: [
      "Engagement letters and mandate summaries",
      "Internal committee memos and decision logs (drafts for human review)",
      "Client-facing project charters tied to recorded expectations",
    ],
  },
  {
    id: "legal-compliance",
    industry: "Legal, risk & corporate compliance",
    summary:
      "Legal teams use QuillAI for first drafts and structured summaries—not as a substitute for professional judgment. Transcripts and templates combine to produce consistent memos and checklists that lawyers still review before filing or sending.",
    painPoints: [
      "Matter intake calls contain facts that must be captured uniformly across documents.",
      "Playbooks exist, but associates still retype structure from scratch.",
      "Cross-border teams need the same issue list after every client update call.",
    ],
    quillHelps: [
      "Generate outline-level drafts from interviews and status calls using firm-approved templates.",
      "Keep citations traceable back to the session or note source for internal QA.",
      "Reduce time spent formatting recurring matter types (status updates, internal briefs).",
    ],
    deliverables: [
      "Matter summaries and internal memos (draft for attorney review)",
      "Due-diligence-style checklists derived from call notes",
      "Client update letters following your house style",
    ],
  },
  {
    id: "life-sciences",
    industry: "Life sciences, healthcare & regulated research",
    summary:
      "Clinical and regulatory workflows depend on accurate, versioned documentation. QuillAI accelerates drafting from study meetings and cross-functional reviews while your quality process remains in control of what gets submitted.",
    painPoints: [
      "Protocol amendments and safety updates require reconciling what multiple functions agreed on in meetings.",
      "Investigator and site communications repeat similar explanations.",
      "Non-clinical teams struggle to turn workshop output into structured study artifacts.",
    ],
    quillHelps: [
      "Draft structured sections from recorded study and operations meetings.",
      "Apply templates that encode required headings, roles, and disclaimers for your org.",
      "Maintain a clear path from session to document for internal traceability (not a substitute for validated systems of record).",
    ],
    deliverables: [
      "Protocol and amendment drafts (for QA review)",
      "Study timelines and operational plans",
      "Cross-functional meeting minutes with action tables",
    ],
  },
  {
    id: "manufacturing-operations",
    industry: "Manufacturing, supply chain & industrials",
    summary:
      "Capital projects and supplier programs generate a stream of meetings—kickoffs, design reviews, and corrective actions. QuillAI turns those conversations into RFQ snippets, project charters, and action trackers your ops team can execute.",
    painPoints: [
      "RFQs and scope documents drift from what was negotiated on the plant floor or in video calls.",
      "Corrective-action discussions need to become written plans quickly.",
      "Program managers re-key the same boilerplate for each vendor or site.",
    ],
    quillHelps: [
      "Capture scope and constraints from supplier and internal program calls.",
      "Use templates for charters, RAID logs, and handover notes.",
      "Keep each program under a customer/project hierarchy for multi-site rollouts.",
    ],
    deliverables: [
      "RFQ and scope summaries",
      "Program kickoff and gate-review packs",
      "CAPA or improvement-plan drafts from review meetings",
    ],
  },
];

/**
 * Public page describing QuillAI use cases by industry (meetings, templates, and deliverables).
 *
 * @returns {React.ReactElement} Full-page use cases content.
 */
const UseCasesPage = () => {
  const scrollToIndustry = (id) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", minHeight: "100vh" }}>
      <MarketingSiteHeader />

      <Box component="main">
        <Box
          sx={{
            py: { xs: 6, md: 10 },
            background: "linear-gradient(135deg, rgba(74,0,224,0.06), rgba(249,72,74,0.06))",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="overline" color="secondary" fontWeight={700}>
              Use cases by industry
            </Typography>
            <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 1, mb: 2, maxWidth: 900 }}>
              Where QuillAI fits: meetings, templates, and client-ready documents
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 800, mb: 3 }}>
              QuillAI is a B2B workspace for customers, projects, and meeting sessions. Below is how different teams
              turn transcripts and notes into structured deliverables—always starting from your templates and reviewed by
              your people before anything goes out the door.
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
              {INDUSTRY_USE_CASES.map((row) => (
                <Chip
                  key={row.id}
                  label={row.industry}
                  onClick={() => scrollToIndustry(row.id)}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Stack spacing={6}>
            {INDUSTRY_USE_CASES.map((row) => (
              <Paper
                key={row.id}
                id={row.id}
                component="section"
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  scrollMarginTop: 96,
                }}
              >
                <Typography variant="h4" component="h2" fontWeight={800} sx={{ mb: 2 }}>
                  {row.industry}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                  {row.summary}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" fontWeight={700} color="secondary" sx={{ mb: 1 }}>
                      Common friction
                    </Typography>
                    <Stack component="ul" sx={{ m: 0, pl: 2.5, color: "text.secondary" }}>
                      {row.painPoints.map((t) => (
                        <Typography key={t} component="li" variant="body2" sx={{ mb: 1 }}>
                          {t}
                        </Typography>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" fontWeight={700} color="secondary" sx={{ mb: 1 }}>
                      How QuillAI helps
                    </Typography>
                    <Stack component="ul" sx={{ m: 0, pl: 2.5, color: "text.secondary" }}>
                      {row.quillHelps.map((t) => (
                        <Typography key={t} component="li" variant="body2" sx={{ mb: 1 }}>
                          {t}
                        </Typography>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" fontWeight={700} color="secondary" sx={{ mb: 1 }}>
                      Example deliverables
                    </Typography>
                    <Stack component="ul" sx={{ m: 0, pl: 2.5, color: "text.secondary" }}>
                      {row.deliverables.map((t) => (
                        <Typography key={t} component="li" variant="body2" sx={{ mb: 1 }}>
                          {t}
                        </Typography>
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>

          <Paper
            elevation={0}
            sx={{
              mt: 6,
              p: 4,
              borderRadius: 3,
              textAlign: "center",
              background: "linear-gradient(135deg, rgba(74,0,224,0.95), rgba(249,72,74,0.88))",
              color: "white",
            }}
          >
            <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
              Ready to connect your meetings to your templates?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
              Start free or sign in to organize customers, projects, and sessions in one workspace.
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              size="large"
              variant="contained"
              color="inherit"
              endIcon={<ArrowForwardIcon />}
              sx={{ color: "primary.main", textTransform: "none", fontWeight: 700 }}
            >
              Get started
            </Button>
          </Paper>
        </Container>
      </Box>

      <MarketingSiteFooter />
    </Box>
  );
};

export default UseCasesPage;
